import type { KeyboardEvent, MouseEvent } from "react";
import React, { memo, useCallback, useRef, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { IHoverTapTooltipProps } from "./types";

// NOTE: NSAI-7345 — a badge whose explanation opens on hover, keyboard focus or
// tap, for statuses whose detail would otherwise sit under the badge as a
// paragraph. Activation follows HoverRevealChip (AL-G19): Radix's trigger
// closes on its own pointerdown and click, so a tap would never open a plain
// tooltip. Hover and focus stay Radix's (`isHoverOpen`); a click, tap, Enter or
// Space pins it open (`isPinned`) until a second activation, Escape, blur or a
// press outside.
const HoverTapTooltipComponent = ({
  label,
  content,
  testId,
  children,
}: IHoverTapTooltipProps): React.ReactElement => {
  const [isPinned, setIsPinned] = useState(false);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  // NOTE: Radix closes an open tooltip from the trigger's pointerdown, so the
  // click toggles off the value snapshotted there, not the current one.
  const wasPinnedAtPointerDownRef = useRef(false);

  const unpin = useCallback((): void => {
    setIsPinned(false);
  }, []);

  const handlePointerDown = useCallback((): void => {
    wasPinnedAtPointerDownRef.current = isPinned;
  }, [isPinned]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      // NOTE: Prevented so Radix skips its own closing `onClick`; stopped so
      // the press never reaches anything the row itself reacts to.
      event.preventDefault();
      event.stopPropagation();
      setIsPinned(!wasPinnedAtPointerDownRef.current);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === "Enter" || event.key === " ") {
        // NOTE: Toggled here rather than through the click the key would
        // fire, which has no pointerdown to snapshot the pin from.
        event.preventDefault();
        setIsPinned((previous) => !previous);
        return;
      }
      if (event.key === "Escape") {
        setIsPinned(false);
      }
    },
    [],
  );

  const isOpen = isPinned || isHoverOpen;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={isOpen} onOpenChange={setIsHoverOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            aria-label={`${label}: ${content}`}
            aria-expanded={isOpen}
            onPointerDown={handlePointerDown}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onBlur={unpin}
            className="inline-flex shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-xs break-words"
          data-testid={`${testId}-tooltip`}
          onPointerDownOutside={unpin}
          onEscapeKeyDown={unpin}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const HoverTapTooltip = memo(HoverTapTooltipComponent);
